import { useState, type FormEvent } from 'react'
import { EstadoError } from '../componentes/EstadoError'
import { EstadoVacio } from '../componentes/EstadoVacio'
import { euros } from '../componentes/formato'
import { AvisoError, CabeceraModal, CabeceraPagina, Campo, CLASE_BOTON, CLASE_BOTON_PRIMARIO, CLASE_INPUT, Insignia } from '../componentes/formulario'
import { IconoAnadir, IconoAviso, IconoBuscar } from '../componentes/iconos'
import { Modal } from '../componentes/Modal'
import { SkeletonList } from '../componentes/Skeleton'
import type { DatosProducto, Producto } from '../api/modelos'
import {
  productosEnvaseQueFaltan,
  useAjustes,
  useCambiarActivoProducto,
  useCatalogo,
  useCrearProducto,
  useEditarProducto,
} from '../hooks/useCatalogo'
import { useEsAdmin } from '../hooks/useUsuarioActual'
import { BarraPaginacion } from '../componentes/BarraPaginacion'
import { usePaginacionLocal } from '../componentes/paginacion'

interface ValoresProducto {
  nombre: string
  referencia: string
  precio: string
  impuesto_pct: string
}

function aNumero(texto: string): number {
  return Number(texto.trim().replace(',', '.'))
}

/** Errores del lado del panel; el backend vuelve a validar. */
function validarProducto(v: ValoresProducto): string[] {
  const errores: string[] = []
  if (!v.nombre.trim()) errores.push('El nombre es obligatorio.')
  const precio = aNumero(v.precio)
  if (v.precio.trim() === '' || !Number.isFinite(precio) || precio < 0) errores.push('El precio tiene que ser un número mayor o igual que 0.')
  const pct = aNumero(v.impuesto_pct)
  if (v.impuesto_pct.trim() === '' || !Number.isFinite(pct) || pct < 0 || pct > 100) errores.push('El impuesto tiene que estar entre 0 y 100.')
  return errores
}

function FormProducto({
  producto,
  onCerrar,
}: {
  producto: Producto | null
  onCerrar: () => void
}) {
  const crear = useCrearProducto()
  const editar = useEditarProducto()
  const mutacion = producto ? editar : crear
  const [v, setV] = useState<ValoresProducto>({
    nombre: producto?.nombre ?? '',
    referencia: producto?.referencia ?? '',
    precio: producto ? String(producto.precio) : '',
    impuesto_pct: producto ? String(producto.impuesto_pct) : '21',
  })
  const [errores, setErrores] = useState<string[]>([])
  const titulo = producto ? `Editar ${producto.nombre}` : 'Nuevo producto'

  const enviar = (e: FormEvent) => {
    e.preventDefault()
    const encontrados = validarProducto(v)
    setErrores(encontrados)
    if (encontrados.length) return
    const datos: DatosProducto = {
      nombre: v.nombre.trim(),
      referencia: v.referencia.trim(),
      precio: aNumero(v.precio),
      impuesto_pct: aNumero(v.impuesto_pct),
    }
    if (producto) editar.mutate({ id: producto.id, datos }, { onSuccess: onCerrar })
    else crear.mutate(datos, { onSuccess: onCerrar })
  }

  return (
    <Modal titulo={titulo} onCerrar={onCerrar}>
      <CabeceraModal titulo={titulo} onCerrar={onCerrar} />
      <form onSubmit={enviar} noValidate className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto px-5 py-4">
        <Campo etiqueta="Nombre *" ayuda="Como lo leerá el resolvedor, p. ej. «BONO 4 18.9L» o «ENVASE 18.9L».">
          <input value={v.nombre} onChange={(e) => setV({ ...v, nombre: e.target.value })} className={CLASE_INPUT} />
        </Campo>
        <Campo etiqueta="Referencia">
          <input value={v.referencia} onChange={(e) => setV({ ...v, referencia: e.target.value })} className={CLASE_INPUT} />
        </Campo>
        <div className="grid gap-3 sm:grid-cols-2">
          <Campo etiqueta="Precio sin impuesto *">
            <input value={v.precio} onChange={(e) => setV({ ...v, precio: e.target.value })} inputMode="decimal" className={CLASE_INPUT} />
          </Campo>
          <Campo etiqueta="Impuesto (%) *">
            <input value={v.impuesto_pct} onChange={(e) => setV({ ...v, impuesto_pct: e.target.value })} inputMode="decimal" className={CLASE_INPUT} />
          </Campo>
        </div>
        {errores.length ? (
          <ul role="alert" className="list-disc rounded-lg border border-atencion-anillo bg-atencion-fondo py-2 pl-8 pr-3 text-sm text-atencion-tinta">
            {errores.map((m) => <li key={m}>{m}</li>)}
          </ul>
        ) : null}
        <AvisoError error={mutacion.error} />
        <div className="flex gap-2">
          <button type="submit" disabled={mutacion.isPending} className={CLASE_BOTON_PRIMARIO}>
            {mutacion.isPending ? 'Guardando' : producto ? 'Guardar cambios' : 'Crear producto'}
          </button>
          <button type="button" onClick={onCerrar} className={CLASE_BOTON}>Cancelar</button>
        </div>
      </form>
    </Modal>
  )
}

/** Aviso del resolvedor: sin envase y devolución, ningún bono se compone. */
function AvisoEnvase({ faltan }: { faltan: string[] }) {
  if (faltan.length === 0) return null
  return (
    <div role="status" className="flex items-start gap-3 rounded-xl border border-atencion-anillo bg-atencion-fondo px-4 py-3 text-sm text-atencion-tinta">
      <IconoAviso aria-hidden="true" className="mt-0.5 size-5 shrink-0" />
      <div>
        <p className="font-semibold">Faltan los productos de envase y devolución que exige el agendamiento</p>
        <p className="mt-1">
          Cada bono se compone con «{'<nombre> <litros>'}» de estos productos. Crea al menos uno activo que empiece por:{' '}
          {faltan.map((n, i) => (
            <span key={n}>
              {i > 0 ? ' y ' : ''}
              <strong>{n}</strong>
            </span>
          ))}
          {' '}(p. ej. «{faltan[0]} 18.9L»). Los nombres se cambian en Ajustes.
        </p>
      </div>
    </div>
  )
}

/**
 * Catálogo de productos (RF-41). Todos lo ven; crear, editar, desactivar y
 * reactivar es de administrador (el backend responde 403 al resto).
 */
export function Catalogo() {
  const admin = useEsAdmin()
  const catalogo = useCatalogo(true)
  // Los nombres de envase vienen de Ajustes; sin permiso para leerlos, los de siempre.
  const ajustes = useAjustes()
  const cambiar = useCambiarActivoProducto()
  const [q, setQ] = useState('')
  const [editando, setEditando] = useState<Producto | null | 'nuevo'>(null)

  const productos = catalogo.data?.items ?? []
  const visibles = productos.filter((p) => p.nombre.toLowerCase().includes(q.trim().toLowerCase()))
  // 15 por página; buscar vuelve a la primera.
  const pag = usePaginacionLocal(visibles, q.trim().toLowerCase())
  const valores = ajustes.data?.valores
  const faltan = catalogo.isSuccess
    ? productosEnvaseQueFaltan(
        productos,
        typeof valores?.pedido_producto_envase === 'string' ? valores.pedido_producto_envase : 'ENVASE',
        typeof valores?.pedido_producto_devolucion === 'string' ? valores.pedido_producto_devolucion : 'DEVOLUCION ENVASE',
      )
    : []

  return (
    <div className="flex flex-col gap-4">
      <CabeceraPagina
        titulo="Catálogo"
        descripcion="Productos con los que se componen los pedidos y los albaranes. Un producto no se borra: se desactiva."
        acciones={
          admin ? (
            <button type="button" className={CLASE_BOTON_PRIMARIO} onClick={() => setEditando('nuevo')}>
              <IconoAnadir aria-hidden="true" className="size-4" />
              Nuevo producto
            </button>
          ) : null
        }
      />

      <AvisoEnvase faltan={faltan} />

      <label className="relative block max-w-md">
        <span className="sr-only">Buscar producto</span>
        <IconoBuscar aria-hidden="true" className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-tinta-apagada" />
        <input type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por nombre" className={`${CLASE_INPUT} pl-9`} />
      </label>

      <AvisoError error={cambiar.error} />

      {catalogo.isPending ? (
        <SkeletonList />
      ) : catalogo.isError ? (
        <EstadoError error={catalogo.error} onReintentar={() => void catalogo.refetch()} />
      ) : visibles.length === 0 ? (
        <EstadoVacio
          titulo={q ? 'Ningún producto coincide' : 'El catálogo está vacío'}
          descripcion={admin ? 'Crea los productos con «Nuevo producto».' : 'Un administrador tiene que dar de alta los productos.'}
        />
      ) : (
        <div className="overflow-x-auto rounded-xl border border-borde bg-superficie">
          <table className="w-full text-left text-sm">
            <thead className="border-b border-borde text-xs uppercase tracking-wide text-tinta-apagada">
              <tr>
                <th className="px-4 py-2 font-semibold">Producto</th>
                <th className="px-4 py-2 font-semibold">Referencia</th>
                <th className="px-4 py-2 text-right font-semibold">Precio</th>
                <th className="px-4 py-2 text-right font-semibold">Impuesto</th>
                <th className="px-4 py-2 text-right font-semibold">Con impuesto</th>
                <th className="px-4 py-2 font-semibold">Estado</th>
                {admin ? <th className="px-4 py-2"><span className="sr-only">Acciones</span></th> : null}
              </tr>
            </thead>
            <tbody className="divide-y divide-borde-suave">
              {pag.visibles.map((p) => (
                <tr key={p.id} className={p.activo ? '' : 'text-tinta-apagada'}>
                  <td className="px-4 py-2 font-medium text-tinta">{p.nombre}</td>
                  <td className="px-4 py-2 text-tinta-2">{p.referencia || '—'}</td>
                  <td className="px-4 py-2 text-right tabular-nums">{euros(p.precio)}</td>
                  <td className="px-4 py-2 text-right tabular-nums">{p.impuesto_pct} %</td>
                  <td className="px-4 py-2 text-right tabular-nums">{euros(p.precio_con_iva)}</td>
                  <td className="px-4 py-2">{p.activo ? <Insignia tono="positivo">Activo</Insignia> : <Insignia tono="neutro">Desactivado</Insignia>}</td>
                  {admin ? (
                    <td className="whitespace-nowrap px-4 py-2 text-right">
                      <button type="button" className={`${CLASE_BOTON} mr-2`} onClick={() => setEditando(p)} aria-label={`Editar ${p.nombre}`}>
                        Editar
                      </button>
                      <button
                        type="button"
                        className={CLASE_BOTON}
                        disabled={cambiar.isPending}
                        onClick={() => cambiar.mutate({ id: p.id, activo: !p.activo })}
                        aria-label={`${p.activo ? 'Desactivar' : 'Reactivar'} ${p.nombre}`}
                      >
                        {p.activo ? 'Desactivar' : 'Reactivar'}
                      </button>
                    </td>
                  ) : null}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <BarraPaginacion pagina={pag.pagina} paginas={pag.paginas} onCambiar={pag.irAPagina} />

      {editando ? <FormProducto producto={editando === 'nuevo' ? null : editando} onCerrar={() => setEditando(null)} /> : null}
    </div>
  )
}
